/**
 * FileRoleSelector Component
 *
 * Allows users to select or override the detected role of a file.
 * Shows auto-detected role with alternatives and allows manual selection.
 *
 * @epic DC-573
 * @task Phase 3 UI Components
 */

import React, { useState } from 'react';
import { FileRoleBadge, type FileRole, type RoleAction } from './FileRoleBadge';

export interface AlternativeRole {
  role: FileRole;
  confidence: number;
}

export interface FileRoleSelectorProps {
  /** Current/detected role */
  currentRole: FileRole;
  /** Confidence in current role (0-1) */
  confidence: number;
  /** Action recommendation */
  action: RoleAction;
  /** Alternative roles that were considered */
  alternativeRoles?: AlternativeRole[];
  /** Callback when role is changed */
  onRoleChange: (newRole: FileRole, source: 'user' | 'auto') => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Filename for display */
  filename?: string;
}

/** All available roles for selection */
const ALL_ROLES: FileRole[] = [
  'primary_data',
  'data_dictionary',
  'metadata',
  'qc_report',
  'crosswalk',
  'license',
  'reference',
  'documentation',
];

/** Role descriptions for help text */
const ROLE_DESCRIPTIONS: Record<FileRole, string> = {
  primary_data: 'Main dataset to be assessed (CSV, Excel, etc.)',
  data_dictionary: 'Column definitions, types, and descriptions',
  metadata: 'Sample information, provenance, or context',
  qc_report: 'Quality control measurements or validation results',
  crosswalk: 'Maps proprietary IDs to standard ontologies',
  license: 'Usage rights and attribution requirements',
  reference: 'Lookup tables, expected values, or standards',
  documentation: 'README, methodology, or protocol documents',
  unknown: 'Role could not be determined',
};

/**
 * FileRoleSelector provides a dropdown to select or override file roles.
 */
export function FileRoleSelector({
  currentRole,
  confidence,
  action,
  alternativeRoles,
  onRoleChange,
  disabled = false,
  filename,
}: FileRoleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<FileRole>(currentRole);

  const handleRoleSelect = (role: FileRole) => {
    setSelectedRole(role);
    onRoleChange(role, 'user');
    setIsOpen(false);
  };

  const handleRevertToAuto = () => {
    setSelectedRole(currentRole);
    onRoleChange(currentRole, 'auto');
    setIsOpen(false);
  };

  // Sort alternatives by confidence
  const sortedAlternatives = [...(alternativeRoles || [])].sort(
    (a, b) => b.confidence - a.confidence
  );

  // Get roles not in alternatives or current
  const otherRoles = ALL_ROLES.filter(
    (r) =>
      r !== currentRole &&
      r !== selectedRole &&
      !sortedAlternatives.some((alt) => alt.role === r)
  );

  return (
    <div className="relative inline-block">
      {/* Current role badge (clickable to open selector) */}
      <div
        className={`cursor-pointer ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <FileRoleBadge
          role={selectedRole}
          confidence={selectedRole === currentRole ? confidence : undefined}
          action={selectedRole === currentRole ? action : 'auto_assign'}
          showConfidence={selectedRole === currentRole}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        />
        {selectedRole !== currentRole && (
          <span className="ml-1 text-xs text-blue-600">(manual)</span>
        )}
      </div>

      {/* Dropdown selector */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
          {/* Header */}
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-700">
              Select Role{filename ? ` for ${filename}` : ''}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Auto-detected: {currentRole} ({(confidence * 100).toFixed(0)}%)
            </p>
          </div>

          {/* Current/detected role */}
          <div className="px-2 py-1">
            <button
              className={`w-full text-left px-2 py-1.5 rounded flex items-center justify-between hover:bg-gray-50 ${
                selectedRole === currentRole ? 'bg-blue-50' : ''
              }`}
              onClick={handleRevertToAuto}
            >
              <div className="flex items-center gap-2">
                <FileRoleBadge role={currentRole} size="sm" showConfidence={false} />
                <span className="text-xs text-green-600">Auto-detected</span>
              </div>
              <span className="text-xs text-gray-500">
                {(confidence * 100).toFixed(0)}%
              </span>
            </button>
          </div>

          {/* Alternative roles from detection */}
          {sortedAlternatives.length > 0 && (
            <>
              <div className="px-3 py-1 text-xs text-gray-500 font-medium">
                Alternatives
              </div>
              {sortedAlternatives.map((alt) => (
                <div key={alt.role} className="px-2 py-0.5">
                  <button
                    className={`w-full text-left px-2 py-1.5 rounded flex items-center justify-between hover:bg-gray-50 ${
                      selectedRole === alt.role ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleRoleSelect(alt.role)}
                  >
                    <FileRoleBadge role={alt.role} size="sm" showConfidence={false} />
                    <span className="text-xs text-gray-500">
                      {(alt.confidence * 100).toFixed(0)}%
                    </span>
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Other available roles */}
          {otherRoles.length > 0 && (
            <>
              <div className="px-3 py-1 text-xs text-gray-500 font-medium border-t border-gray-100 mt-1 pt-2">
                Other Roles
              </div>
              {otherRoles.map((role) => (
                <div key={role} className="px-2 py-0.5">
                  <button
                    className={`w-full text-left px-2 py-1.5 rounded flex items-center justify-between hover:bg-gray-50 ${
                      selectedRole === role ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleRoleSelect(role)}
                  >
                    <FileRoleBadge role={role} size="sm" showConfidence={false} />
                    <span className="text-xs text-gray-400 max-w-32 truncate">
                      {ROLE_DESCRIPTIONS[role]}
                    </span>
                  </button>
                </div>
              ))}
            </>
          )}

          {/* Help text */}
          <div className="px-3 py-2 border-t border-gray-100 mt-1">
            <p className="text-xs text-gray-500">
              {ROLE_DESCRIPTIONS[selectedRole]}
            </p>
          </div>
        </div>
      )}

      {/* Backdrop to close dropdown */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Inline role selector for compact spaces
 */
export function InlineRoleSelector({
  currentRole,
  onRoleChange,
  disabled = false,
}: {
  currentRole: FileRole;
  onRoleChange: (role: FileRole) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={currentRole}
      onChange={(e) => onRoleChange(e.target.value as FileRole)}
      disabled={disabled}
      className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white"
    >
      {ALL_ROLES.map((role) => (
        <option key={role} value={role}>
          {role.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

export default FileRoleSelector;
